import { SideNavContainer } from './SideNavContainer'
import { SideNavHeader } from './SideNavHeader'
import { SideNavSection, SideNavDivider, SideNavSpacer } from './SideNavSection'
import { SideNavFooter } from './SideNavFooter'
import { SideNavItem } from '../side-nav-item/SideNavItem'
import { SideNavGroup } from '../side-nav-item/SideNavGroup'
import { SideNavSubItem } from '../side-nav-item/SideNavSubItem'

// =============================================================================
// Shared Types
// =============================================================================

/**
 * Configuration for a single navigation item in the sidebar.
 *
 * Used to define clickable navigation links with icons, labels, and optional
 * indicators like lock icons (for anonymous users) or beta tags.
 */
export type SideNavItemConfig = {
  /** Unique identifier for the item, used for active state matching */
  id: string
  /** Display text for the navigation item */
  label: string
  /** Navigation target URL */
  href: string
  /** Icon component to display alongside the label */
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  /** Shows a lock icon indicating the item requires authentication */
  showLock?: boolean
  /** Shows a beta tag next to the item */
  showBetaTag?: boolean
  /** Additional classes to apply to the icon */
  iconClassName?: string
  /** Optional click handler for item-specific actions (runs before navigation) */
  onClick?: () => void
}

/**
 * Configuration for a collapsible group of navigation items.
 *
 * Used for grouping related features (e.g., Medical Overview containing
 * Chronology and Billing Summary).
 */
export type SideNavGroupConfig = {
  /** Unique identifier for the group */
  id: string
  /** Display text for the group header */
  label: string
  /** Icon component to display in the group header */
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  /** Navigation items nested within this group */
  items: SideNavItemConfig[]
  /** Shows a beta tag next to the group header */
  showBetaTag?: boolean
}

/**
 * Configuration for a collapsible nav group with expand/collapse state.
 *
 * Extends SideNavGroupConfig with state management for the expanded/collapsed state.
 */
export type SideNavCollapsibleGroupConfig = SideNavGroupConfig & {
  /** Whether the group is currently expanded */
  isExpanded: boolean
  /** Callback when the expand/collapse toggle is clicked */
  onExpandToggle: () => void
}

// =============================================================================
// SideNavDashboard - Dashboard navigation (Matters, Templates)
// =============================================================================

export type SideNavDashboardProps = {
  /** Navigation items to display */
  items: SideNavItemConfig[]
  /** ID of the currently active item for highlight state */
  activeItemId?: string
  /** Called after any nav item click completes (e.g., to close mobile sidebar) */
  onNavigate?: () => void
  /** Render prop for footer content (account menu, support, etc.) */
  renderFooter: () => React.ReactNode
  /** Logo URL */
  logoUrl: string
  /** Logo alt text */
  logoAlt?: string
  /** Whether to apply fixed height sizing to the logo (34px) */
  hasFixedLogoHeight?: boolean
  /** Optional callback when logo is clicked (e.g., to close mobile sidebar) */
  onLogoClick?: () => void
}

/**
 * Dashboard variant of the SideNav for root-level navigation.
 *
 * Displays a flat list of navigation items (e.g., Matters, Templates).
 */
export function SideNavDashboard({
  items,
  activeItemId,
  onNavigate,
  renderFooter,
  logoUrl,
  logoAlt = 'Logo',
  hasFixedLogoHeight = false,
  onLogoClick,
}: SideNavDashboardProps) {
  return (
    <SideNavContainer data-ds-component="SideNav" data-ds-variant="dashboard">
      <SideNavHeader logoUrl={logoUrl} logoAlt={logoAlt} hasFixedLogoHeight={hasFixedLogoHeight} onLogoClick={onLogoClick} />

      <nav className="flex flex-1 flex-col">
        <SideNavSpacer />

        <SideNavSection className="flex-1">
          <div className="flex flex-col gap-4 px-2">
            {items.map((item) => (
              <SideNavItem
                key={item.id}
                to={item.href}
                icon={item.icon}
                label={item.label}
                isActive={activeItemId === item.id}
                showLock={item.showLock}
                showBetaTag={item.showBetaTag}
                onClick={() => {
                  item.onClick?.()
                  onNavigate?.()
                }}
                iconClassName={item.iconClassName}
              />
            ))}
          </div>

          <SideNavFooter>{renderFooter()}</SideNavFooter>
        </SideNavSection>
      </nav>
    </SideNavContainer>
  )
}

// =============================================================================
// SideNavMatter - Matter context navigation (AI Assistant, Medical, etc.)
// =============================================================================

export type SideNavMatterProps = {
  /** Top section items (e.g., Home, Matter Overview, AI Assistant, Matter Files) */
  topItems: SideNavItemConfig[]
  /** Optional collapsible group below divider (e.g., Medical Overview) */
  collapsibleGroup?: SideNavCollapsibleGroupConfig
  /** ID of the currently active item for highlight state */
  activeItemId?: string
  /** Called after any nav item click completes (e.g., to close mobile sidebar) */
  onNavigate?: () => void
  /** Render prop for footer content (account menu, support, etc.) */
  renderFooter: () => React.ReactNode
  /** Logo URL */
  logoUrl: string
  /** Logo alt text */
  logoAlt?: string
  /** Whether to apply fixed height sizing to the logo (34px) - Prev isPaxtonLogo */
  hasFixedLogoHeight?: boolean
  /** Optional callback when logo is clicked (e.g., to close mobile sidebar) */
  onLogoClick?: () => void
}

/**
 * Matter variant of the SideNav for navigation within a matter context.
 *
 * Displays a structured layout with:
 * - Top items (Home, Matter Overview, AI Assistant, Matter Files)
 * - Divider
 * - Collapsible group (e.g., Medical Overview with Chronology and Billing)
 */
export function SideNavMatter({
  topItems,
  collapsibleGroup,
  activeItemId,
  onNavigate,
  renderFooter,
  logoUrl,
  logoAlt = 'Logo',
  hasFixedLogoHeight = false,
  onLogoClick,
}: SideNavMatterProps) {
  return (
    <SideNavContainer data-ds-component="SideNav" data-ds-variant="matter">
      <SideNavHeader logoUrl={logoUrl} logoAlt={logoAlt} hasFixedLogoHeight={hasFixedLogoHeight} onLogoClick={onLogoClick} />

      <nav className="flex flex-1 flex-col">
        <SideNavSpacer />

        <SideNavSection>
          <div className="flex flex-col gap-4 px-2 pb-1">
            {/* Top items */}
            {topItems.map((item) => (
              <SideNavItem
                key={item.id}
                to={item.href}
                icon={item.icon}
                label={item.label}
                isActive={activeItemId === item.id}
                showLock={item.showLock}
                showBetaTag={item.showBetaTag}
                onClick={() => {
                  item.onClick?.()
                  onNavigate?.()
                }}
                iconClassName={item.iconClassName}
              />
            ))}
          </div>
        </SideNavSection>

        {collapsibleGroup && <SideNavDivider />}

        <SideNavSection className="flex-1">
          {/* Collapsible group */}
          {collapsibleGroup && (
            <div className="px-2">
              <SideNavGroup
                icon={collapsibleGroup.icon}
                label={collapsibleGroup.label}
                isExpanded={collapsibleGroup.isExpanded}
                onExpandToggle={collapsibleGroup.onExpandToggle}
                showBetaTag={collapsibleGroup.showBetaTag}
              >
                {collapsibleGroup.items.map((item) => (
                  <SideNavSubItem
                    key={item.id}
                    to={item.href}
                    icon={item.icon}
                    label={item.label}
                    isActive={activeItemId === item.id}
                    showBetaTag={item.showBetaTag}
                    onClick={onNavigate}
                  />
                ))}
              </SideNavGroup>
            </div>
          )}

          <SideNavFooter>{renderFooter()}</SideNavFooter>
        </SideNavSection>
      </nav>
    </SideNavContainer>
  )
}
